import React, { Suspense, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider } from './context/AuthContext';
import { SiteProvider, useSite } from './context/SiteContext';
import { useAuth } from './context/AuthContext';
import { popupAPI } from './services/api';
import MaintenancePage from './pages/public/MaintenancePage';
import './styles/variables.css';
import './components/common/Common.css';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

// Public pages
import HomePage        from './pages/public/HomePage';
import CataloguePage   from './pages/public/CataloguePage';
import StudioPage      from './pages/public/StudioPage';
import { BlogPage, BlogDetailPage } from './pages/public/BlogPages';
import { ContactPage, FAQPage, LegalPage, TechniquesPage } from './pages/public/PublicPages';

// Admin layout
import { AdminLayout, AdminLoginPage, ProtectedRoute } from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';

// Admin pages v1
import { AdminOrdersPage } from './pages/admin/AdminSalesPages';
import { AdminBlogPage }   from './pages/admin/AdminPages';

// Admin pages v2 — Enhanced
import { AdminMediaPage } from './pages/admin/AdminEnhanced';

// Admin pages — Document editor (factures + devis avec PDF)
import { AdminInvoicesFullPage, AdminQuotesFullPage, InvoiceEditPage, InvoiceNewPage, QuoteEditPage, QuoteNewPage } from './pages/admin/AdminDocumentEditor';

// Admin pages v3 — Enhanced2
import { AdminClientsEnhancedPage, AdminSettingsEnhancedPage, AdminServicesPage } from './pages/admin/AdminEnhanced2';

// Admin pages — catalogue management tab
import { AdminProductsWithCatalogPage } from './pages/admin/AdminProductsCatalog';
import SignaturePage from './pages/public/SignaturePage';
import ExtranetPortalPage from './pages/public/ExtranetPortalPage';
import AdminEmailPage from './pages/admin/AdminEmailPage';
import AdminExtranetPage from './pages/admin/AdminExtranetPage';
import AdminSignaturesPage from './pages/admin/AdminSignaturesPage';

// Lazy loaded
const AdminNewsletterPage = React.lazy(() => import('./pages/admin/AdminNewsletterPage'));
const AdminMessagesPage   = React.lazy(() => import('./pages/admin/AdminMessagesPage'));
const AdminMockupsPage    = React.lazy(() => import('./pages/admin/AdminMockupsPage'));
const AdminPaymentsPage   = React.lazy(() => import('./pages/admin/AdminPaymentsPage'));
const AdminReportsPage    = React.lazy(() => import('./pages/admin/AdminReportsPage'));
const AdminFaqPage        = React.lazy(() => import('./pages/admin/AdminFaqPage'));
const ProductDetailPage   = React.lazy(() => import('./pages/public/ProductDetailPage'));
const QuickQuotePage      = React.lazy(() => import('./pages/public/QuickQuotePage'));

/* ── Error Boundary ─────────────────────────────── */
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('[ErrorBoundary]', error, info); }
  render() {
    if (this.state.hasError) return (
      <div style={{ padding: 40, textAlign: 'center', fontFamily: 'sans-serif' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
        <h2 style={{ marginBottom: 8 }}>Une erreur est survenue</h2>
        <p style={{ color: '#666', marginBottom: 20 }}>{this.state.error?.message}</p>
        <button onClick={() => window.location.href = '/admin'}
          style={{ padding: '10px 24px', background: '#e63946', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>
          Retour au tableau de bord
        </button>
      </div>
    );
    return this.props.children;
  }
}

/* ── Helpers ────────────────────────────────────── */
const Spinner = () => <div className="page-loader" style={{ minHeight: '60vh' }}><div className="spinner spinner-lg" /></div>;

/* ── Garde maintenance ──────────────────────────── */
const MaintenanceGuard = ({ children }) => {
  const { settings, loaded: settingsLoaded } = useSite();
  const { user, loading: authLoading } = useAuth();
  if (!settingsLoaded || authLoading) return <Spinner />;
  const mode    = settings.maintenance_mode;
  const isOn    = mode === true || mode === '1' || mode === 1;
  const isAdmin = user && ['admin', 'super_admin'].includes(user.role);
  if (isOn && !isAdmin) return <MaintenancePage />;
  return <>{children}</>;
};

/* ── Popups site ────────────────────────────────── */
const POPUP_COLORS = { info: '#3b82f6', warning: '#f59e0b', success: '#10b981' };
const DISMISSED_KEY = 'artimarq_dismissed_popups';

const SitePopups = () => {
  const [queue,   setQueue]   = useState([]);
  const [current, setCurrent] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    popupAPI.listActive()
      .then(({ data }) => {
        const dismissed = JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]');
        const toShow = (data.data || []).filter(p => !dismissed.includes(String(p.id)));
        setQueue(toShow);
        if (toShow.length > 0) { setCurrent(toShow[0]); setVisible(true); }
      })
      .catch(() => {});
  }, []);

  const dismiss = () => {
    if (current?.show_once) {
      const dismissed = JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]');
      localStorage.setItem(DISMISSED_KEY, JSON.stringify([...dismissed, String(current.id)]));
    }
    setVisible(false);
    setTimeout(() => {
      const remaining = queue.slice(1);
      setQueue(remaining);
      if (remaining.length > 0) { setCurrent(remaining[0]); setVisible(true); }
      else setCurrent(null);
    }, 300);
  };

  if (!current || !visible) return null;
  const accentColor = POPUP_COLORS[current.type] || POPUP_COLORS.info;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9990,
      background: 'rgba(0,0,0,.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1.5rem',
      animation: 'fadeIn .2s ease',
    }} onClick={dismiss}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 12,
          boxShadow: '0 20px 60px rgba(0,0,0,.18)',
          maxWidth: 480, width: '100%',
          overflow: 'hidden',
          transform: 'translateY(0)',
          animation: 'slideUp .25s ease',
        }}
      >
        {/* Bande colorée */}
        <div style={{ height: 4, background: accentColor }} />
        <div style={{ padding: '1.75rem 2rem 2rem' }}>
          <h3 style={{ margin: '0 0 .75rem', fontSize: 18, fontWeight: 700, color: '#111', paddingRight: 24 }}>
            {current.title}
          </h3>
          <p style={{ margin: '0 0 1.5rem', fontSize: 14, lineHeight: 1.65, color: '#555', whiteSpace: 'pre-line' }}>
            {current.message}
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            {current.button_text && current.button_url && (
              <a
                href={current.button_url}
                target="_blank" rel="noopener noreferrer"
                onClick={dismiss}
                style={{
                  padding: '9px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600,
                  background: accentColor, color: '#fff',
                  textDecoration: 'none', border: 'none', cursor: 'pointer',
                }}
              >
                {current.button_text}
              </a>
            )}
            <button
              onClick={dismiss}
              style={{
                padding: '9px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600,
                background: '#f3f4f6', color: '#374151',
                border: 'none', cursor: 'pointer',
              }}
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PublicLayout = ({ children }) => (
  <MaintenanceGuard>
    <Header />
    <div style={{ paddingTop: 'var(--header-h)' }}>{children}</div>
    <Footer />
    <SitePopups />
  </MaintenanceGuard>
);
const StudioLayout = ({ children }) => <><Header />{children}</>;

function App() {
  return (
    <BrowserRouter>
      <SiteProvider>
        <AuthProvider>
          <Suspense fallback={<Spinner />}>
            <Routes>
              {/* ── Public ── */}
              <Route path="/"                           element={<PublicLayout><HomePage /></PublicLayout>} />
              <Route path="/catalogue"                  element={<PublicLayout><CataloguePage /></PublicLayout>} />
              <Route path="/catalogue/:categorySlug"    element={<PublicLayout><CataloguePage /></PublicLayout>} />
              <Route path="/catalogue/:categorySlug/:productSlug" element={<PublicLayout><ProductDetailPage /></PublicLayout>} />
              <Route path="/produit/:slug" element={<PublicLayout><ProductDetailPage /></PublicLayout>} />
              <Route path="/studio/:productId"          element={<StudioLayout><StudioPage /></StudioLayout>} />
              <Route path="/techniques"                 element={<PublicLayout><TechniquesPage /></PublicLayout>} />
              <Route path="/blog"                       element={<PublicLayout><BlogPage /></PublicLayout>} />
              <Route path="/blog/:slug"                 element={<PublicLayout><BlogDetailPage /></PublicLayout>} />
              <Route path="/faq"                        element={<PublicLayout><FAQPage /></PublicLayout>} />
              <Route path="/contact"                    element={<PublicLayout><ContactPage /></PublicLayout>} />
              <Route path="/devis-rapide"               element={<PublicLayout><QuickQuotePage /></PublicLayout>} />
              <Route path="/sign/:token"     element={<SignaturePage />} />
              <Route path="/extranet/:token" element={<ExtranetPortalPage />} />
              <Route path="/mentions-legales"           element={<PublicLayout><LegalPage settingKey="legal_content"   title="Mentions légales" /></PublicLayout>} />
              <Route path="/protection-des-donnees"     element={<PublicLayout><LegalPage settingKey="privacy_content" title="Protection des données & Cookies" /></PublicLayout>} />
              <Route path="/cgv"                        element={<PublicLayout><LegalPage settingKey="cgv_content"     title="Conditions Générales de Vente" /></PublicLayout>} />

              {/* ── Admin ── */}
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route path="/admin" element={<ErrorBoundary><ProtectedRoute><AdminLayout /></ProtectedRoute></ErrorBoundary>}>
                <Route index           element={<AdminDashboard />} />
                <Route path="orders"   element={<AdminOrdersPage />} />
                <Route path="quotes"   element={<AdminQuotesFullPage />} />
                <Route path="quotes/new"      element={<QuoteNewPage />} />
                <Route path="quotes/:id/edit" element={<QuoteEditPage />} />
                <Route path="invoices" element={<AdminInvoicesFullPage />} />
                <Route path="invoices/new"      element={<InvoiceNewPage />} />
                <Route path="invoices/:id/edit" element={<InvoiceEditPage />} />
                <Route path="payments" element={<AdminPaymentsPage />} />
                <Route path="clients"  element={<AdminClientsEnhancedPage />} />
                <Route path="products" element={<ProtectedRoute roles={['super_admin','admin']}><AdminProductsWithCatalogPage /></ProtectedRoute>} />
                <Route path="mockups"  element={<ProtectedRoute roles={['super_admin','admin']}><AdminMockupsPage /></ProtectedRoute>} />
                <Route path="media"    element={<ProtectedRoute roles={['super_admin','admin','collaborator']}><AdminMediaPage /></ProtectedRoute>} />
                <Route path="blog"     element={<AdminBlogPage />} />
                <Route path="newsletter" element={<AdminNewsletterPage />} />
                <Route path="messages"   element={<AdminMessagesPage />} />
                <Route path="faq"        element={<AdminFaqPage />} />
                <Route path="/admin/emails"    element={<ProtectedRoute><AdminEmailPage /></ProtectedRoute>} />
                <Route path="/admin/extranets"   element={<ProtectedRoute><AdminExtranetPage /></ProtectedRoute>} />
                <Route path="/admin/signatures" element={<ProtectedRoute><AdminSignaturesPage /></ProtectedRoute>} />
                <Route path="reports"    element={<ProtectedRoute roles={['super_admin','admin']}><AdminReportsPage /></ProtectedRoute>} />
                <Route path="settings"   element={<ProtectedRoute roles={['super_admin','admin']}><AdminSettingsEnhancedPage /></ProtectedRoute>} />
                <Route path="services"   element={<ProtectedRoute><AdminServicesPage /></ProtectedRoute>} />
              </Route>

              {/* ── 404 ── */}
              <Route path="*" element={
                <PublicLayout>
                  <div style={{ minHeight: '70vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-lg)', padding: 'var(--space-3xl)' }}>
                    <div style={{ fontSize: 80, fontWeight: 900, color: 'var(--border)' }}>404</div>
                    <h1>Page introuvable</h1>
                    <p style={{ color: 'var(--text-muted)' }}>La page demandée n'existe pas.</p>
                    <a href="/" className="btn btn-primary btn-lg">Retour à l'accueil</a>
                  </div>
                </PublicLayout>
              } />
            </Routes>
          </Suspense>
          <ToastContainer position="top-right" autoClose={4000} theme="light" />
        </AuthProvider>
      </SiteProvider>
    </BrowserRouter>
  );
}

export default App;
